import { useEffect } from "react";

import { api, background } from "../api/client";
import type { LicenceStatus } from "../api/types";
import { useUi } from "../state/ui";

/** Usage de l'interface (administration des licences, demande d'Alexis du 02/11/2026) : vues ouvertes et temps passé,
 *  envoyés par lots au serveur (`POST /api/usage`), un battement toutes les 60 s quand l'onglet est visible et actif.
 *  Jamais le contenu d'un dossier : la clé de la vue, un nombre d'ouvertures, des secondes. Exports, requêtes à l'IA
 *  et dossiers créés sont comptés par le serveur lui-même. */

export const BEAT_MS = 60_000;

export interface UsageItem {
  view: string;
  opens: number;
  seconds: number;
}

/** Compteur d'un onglet : le temps n'est compté que pendant qu'il est actif (visible et au premier plan). */
export class UsageMeter {
  private view: string | null = null;
  private since: number | null = null;
  private items = new Map<string, { opens: number; ms: number }>();

  constructor(private active: boolean) {}

  private settle(now: number): void {
    if (this.active && this.view && this.since !== null) {
      const entry = this.entry(this.view);
      entry.ms += Math.max(0, now - this.since);
    }
    this.since = this.active ? now : null;
  }

  private entry(view: string): { opens: number; ms: number } {
    let entry = this.items.get(view);
    if (!entry) {
      entry = { opens: 0, ms: 0 };
      this.items.set(view, entry);
    }
    return entry;
  }

  /** Vue ouverte (clé stable) : une ouverture comptée à chaque changement. */
  open(view: string, now: number): void {
    this.settle(now);
    if (view !== this.view) this.entry(view).opens += 1;
    this.view = view;
  }

  /** Onglet devenu actif ou inactif. */
  setActive(active: boolean, now: number): void {
    this.settle(now);
    this.active = active;
    this.since = active ? now : null;
  }

  get current(): string | null {
    return this.view;
  }

  get isActive(): boolean {
    return this.active;
  }

  /** Lot à envoyer (secondes entières, le reste gardé pour le lot suivant), vidé ; null s'il n'y a rien. */
  take(now: number): UsageItem[] | null {
    this.settle(now);
    const out: UsageItem[] = [];
    for (const [view, entry] of this.items) {
      const seconds = Math.floor(entry.ms / 1000);
      if (seconds > 0 || entry.opens > 0) out.push({ view, opens: entry.opens, seconds });
      entry.ms -= seconds * 1000;
      entry.opens = 0;
    }
    for (const [view, entry] of [...this.items]) if (!entry.opens && entry.ms < 1000 && view !== this.view) this.items.delete(view);
    return out.length ? out : null;
  }
}

/** Clé de la vue affichée : l'accueil, ou la vue ouverte de l'espace de travail. */
export function viewKeyOf(state: { welcome: boolean; view: string }): string {
  return state.welcome ? "home" : state.view;
}

function tabActive(): boolean {
  if (typeof document === "undefined") return false;
  return document.visibilityState === "visible" && document.hasFocus();
}

/** Suivi monté par le portail de licence, application ouverte : rien hors contrôle de licence. */
export function UsageTracker({ status }: { status: LicenceStatus }) {
  const tracked = status.enforced && status.licensed && !status.suspended;
  useEffect(() => {
    if (!tracked || typeof window === "undefined") return;
    const meter = new UsageMeter(tabActive());
    meter.open(viewKeyOf(useUi.getState()), Date.now());
    const flush = () => {
      const items = meter.take(Date.now());
      if (!items && !meter.isActive) return;
      // Battement de fond : il ne compte pas comme une activité de la personne (docs/17 §4.5) ; un refus est ignoré.
      void background(() => api.postUsage({ items: items ?? [], current: meter.current })).catch(() => undefined);
    };
    const unsubscribe = useUi.subscribe((state) => {
      const key = viewKeyOf(state);
      if (key !== meter.current) meter.open(key, Date.now());
    });
    const onActivity = () => {
      const active = tabActive();
      if (active !== meter.isActive) {
        meter.setActive(active, Date.now());
        if (!active) flush(); // temps de la vue envoyé quand l'onglet passe en arrière-plan
      }
    };
    // Premier signe de vie aussitôt : le poste paraît « en ce moment » dès l'ouverture.
    if (meter.isActive) flush();
    const timer = window.setInterval(() => {
      if (meter.isActive) flush();
    }, BEAT_MS);
    document.addEventListener("visibilitychange", onActivity);
    window.addEventListener("focus", onActivity);
    window.addEventListener("blur", onActivity);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onActivity);
      window.removeEventListener("focus", onActivity);
      window.removeEventListener("blur", onActivity);
      unsubscribe();
    };
  }, [tracked]);
  return null;
}
